/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import { renderErrorMsg } from "../../../assets/component/renderErrorMsg.js";
import ToggleSwitch from "../../../assets/component/ToggleSwitch/ToggleSwitch.js";
import { mapPaymentMethodDataStatus, mapPaymentMethodData } from "../../../assets/helper.js";
import PropTypes from "prop-types";
import UpgradeNoticeCard from "./UpgradeNoticeCard";

const paymentMethods = [
  {
    key: "cash",
    label: "Cash",
  }
];

const defaultFormData = {
  cashOrgName: '',
  cashInstructions: '',
};

class RenderCash extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isEditMode: false,
      formData: defaultFormData,
    };

    this.handleToggleEditMode = this.handleToggleEditMode.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.mapFormDataFromProps = this.mapFormDataFromProps.bind(this);
    this.mapFieldText = this.mapFieldText.bind(this);
    this.renderErrorMsg = this.renderErrorMsg.bind(this);
  }

  componentDidMount() {
    const newFormData = this.mapFormDataFromProps();

    this.setState({
      formData: newFormData,
    });
  }

  handleToggleEditMode() {
    const { isEditMode } = this.state;

    const newState = !isEditMode;

    if (newState === false) {
      // reset form data when cancel edit
      const newFormData = this.mapFormDataFromProps();
      this.setState({
        formData: newFormData,
      });
    }

    this.setState({
      isEditMode: newState,
    });
  }

  handleInputChange(e) {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        [name]: value,
      },
    }));
  }

  handleSave() {
    const { formData } = this.state;
    const { func } = this.props;

    func.handleUpdatePaymentData(paymentMethods[0].key, paymentMethods[0].label, formData);

    this.setState({ isEditMode: false });
  }

  mapFormDataFromProps() {
    const { state } = this.props;
    const { formData } = this.state;

    let newFormData = formData;

    const paymentCodes = state.organizationObj.paymentMethodCode;

    if (paymentCodes) {
      const getPaymentData = paymentCodes[paymentMethods[0].key] || null;

      if (getPaymentData) {
        // reset to latest data from props
        newFormData = {
          cashOrgName: getPaymentData.cashOrgName || '',
          cashInstructions: getPaymentData.cashInstructions || '',
        };
      } else {
        // reset to default
        newFormData = defaultFormData;
      }
    }

    return newFormData;
  }

  mapFieldText(field)  {
    const { formData } = this.state;

    let text = '-';

    if (formData[field] !== '') {
      text = formData[field];
    }

    return text;
  }

  renderErrorMsg() {
    const { state } = this.props;
    const errMsg = state.errMsgInfo.paymentCode;

    if (errMsg.method === paymentMethods[0].key && !errMsg.isCode) {
      return renderErrorMsg(errMsg.msg);
    }

    return null;
  }

  t(key, fallback) {
    const pmText = (this.props.languageObj && this.props.languageObj.PaymentMethods) || {};
    return pmText[key] || fallback;
  }

  render() {
    const { state, func } = this.props;
    const { isEditMode, formData } = this.state;

    const cashStatus = mapPaymentMethodDataStatus(state.organizationObj.paymentMethodCode, paymentMethods[0].key);
    const cashData = mapPaymentMethodData(state.organizationObj.paymentMethodCode, paymentMethods[0].key);
    // Cash is configured if organization name or instructions have data
    const cashIsConfigured = Boolean(cashData.cashOrgName || cashData.cashInstructions);

    if (state.loading) {
      return (
        <div className="RenderCash-loading">
          <div className="RenderCash-spinner"></div>
        </div>
      );
    }

    const currentPlan = state.organizationObj.currentPlan
      ? state.organizationObj.currentPlan.toLowerCase()
      : "";
    const isPremiumPlan = currentPlan === "premium";

    if (!isPremiumPlan) {
      return (
        <div className="RenderCash-container">
          <UpgradeNoticeCard onUpgradeClick={func.redirectToOrgBilling} />
        </div>
      );
    }

    return (
      <div className="RenderCash-container">
        <div className="RenderCash-content">
          <div className="RenderCash-toggleRow">
            <span className="RenderCash-toggleLabel">{this.t("enableCash", "Enable Cash")}</span>
            <ToggleSwitch
              checked={cashStatus.checked}
              onChange={(e) => func.handleUpdatePaymentStatus(e, paymentMethods[0].key)}
              disabled={!cashIsConfigured}
              disabledMessage={!cashIsConfigured ? this.t("fillRequiredFirst", "Please fill in the required fields first") : ""}
            />
          </div>

          <div className="RenderCash-formContainer">
            <div className="RenderCash-savedCredentials">

              <div className="RenderCash-group">
                <div className="RenderCash-subheading">{this.t("orgNameLabel", "Organization Name")}</div>
                {isEditMode ? (
                  <div className="RenderCash-inputFieldContainer">
                    <input
                      type="text"
                      name="cashOrgName"
                      className="RenderCash-inputField"
                      placeholder={this.t("orgNameLabel", "Organization Name")}
                      value={formData.cashOrgName}
                      onChange={this.handleInputChange}
                    />
                  </div>
                ) : (
                  <div className="RenderCash-textValue">
                    {this.mapFieldText('cashOrgName')}
                  </div>
                )}
              </div>
             
              <div className="RenderCash-group">
                <div className="RenderCash-subheading">{this.t("instructions", "Instructions")}</div>
                {isEditMode ? (
                  <div className="RenderCash-textAreaFieldContainer">
                    <textarea
                      type="text"
                      name="cashInstructions"
                      className="RenderCash-textAreaField"
                      placeholder={this.t("instructions", "Instructions")}
                      value={formData.cashInstructions}
                      onChange={this.handleInputChange}
                    />
                  </div>
                ) : (
                  <div className="RenderCash-textValue">
                    {this.mapFieldText('cashInstructions')}
                  </div>
                )}
              </div>

            </div>
          </div>

          {this.renderErrorMsg()}
        </div>
      </div>
    );
  }
}

RenderCash.propTypes = {
  state: PropTypes.object.isRequired,
  func: PropTypes.object.isRequired,
  languageObj: PropTypes.object,
};

export default RenderCash;
